/**
    * @description      : 
    * @author           : 
    * @group            : 
    * @created          : 10/02/2026 - 16:40:23
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 10/02/2026
    * - Author          : 
    * - Modification    : 
**/

import React, { useEffect, useRef, useState } from "react";
import images from "../../images/index";
import {
  Button,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Form,
  Modal,
} from "react-bootstrap";
import Dropdown from "react-bootstrap/Dropdown";
import { Image } from "react-bootstrap";

import {
  doc,
  updateDoc,
  addDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { db, storage } from "../../firebase";
import { useNavigate } from "react-router-dom";
import { auth } from "../../firebase";
import { RxCaretDown } from "react-icons/rx";
import { FiUpload } from "react-icons/fi";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { toast } from "react-toastify";
import { IconType } from 'react-icons';
import IconWrapper from '../../utils/IconWrapper';
import { useTranslation } from 'react-i18next';

import { MdOutlineDriveFileRenameOutline } from "react-icons/md";
interface ActiveCardProps {
  data?: any;
  onStatusChange?: any;
}

const ActiveCard: React.FC<ActiveCardProps> = ({ data, onStatusChange }) => {
  const { t } = useTranslation(['activeCards', 'common']);
  const navigate = useNavigate();
  const [reportList, setReportList] = useState<any[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [show, setShow] = useState(false);
  const [newName, setNewName] = useState<string>(" ");
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRelaunchModal, setShowRelaunchModal] = useState(false);

  const handleClose = () => setShow(false); 
  const handleShow = () => {
    setShow(true);
    setNewName(data.projectName);
  };

  const handleClick = (id: string) => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
      fileInputRef.current.setAttribute("data-id", id); // Store the ID in a data attribute
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files ? event.target.files[0] : null;
    const id = event.target.getAttribute("data-id"); // Retrieve the ID from the data attribute
    toast.warn(t('common:notifications.updatingImage', "Updating project image"));
    if (file && id) {
      try {
        const storageRef = ref(storage, `projectsImage/${id}`);
        const snapshot = await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);

        // Store the download URL in Firestore
        const userDocRef = doc(db, "projects", id);
        await updateDoc(userDocRef, { projectPhoto: downloadURL });
        onStatusChange();
        toast.success(t('common:notifications.imageUpdated', "Image uploaded successfully"));
      } catch (error) {
        toast.error(t('common:notifications.imageUpdateError', "Error uploading image"));
        console.error("Error uploading image:", error);
      }
    }
  };

  // Move project to the archive
  const moveToArchieve = async () => {
    setShowArchiveModal(false);
    const projectRef = doc(db, "projects", data.id);

    await updateDoc(projectRef, {
      status: "archieve",
    });
    onStatusChange();
    toast.success(t('common:notifications.projectArchived', "Project archived successfully"));
  };
  
  // Move project to the trash
  const moveToTrash = async () => {
    setShowDeleteModal(false);
    const projectRef = doc(db, "projects", data.id);
    await updateDoc(projectRef, {
      status: "trash",
    });
    onStatusChange();
    toast.success(t('common:notifications.projectTrashed', "Project moved to trash"));
  };

  // Duplicate project
  const duplicateProject = async () => {
    try {
      // Get the copy text in the current UI language
      const copySuffix = t('common:copySuffix', "(Copy)");
      
      await addDoc(collection(db, "projects"), {
        ...data,
        dublicated: true,
        projectName: `${data.projectName} ${copySuffix}`,
      });
      onStatusChange();
      toast.success(t('common:notifications.projectDuplicated', "Project duplicated successfully"));
    } catch (error) {
      console.error("Error duplicating project:", error);
      toast.error(t('common:notifications.duplicateError', "Error duplicating project"));
    }
  };
  
  useEffect(() => {
    const getReportList = async () => {
      if (auth.currentUser && data.id) {
        const q = query(
          collection(db, "paidReport"),
          where("userId", "==", auth.currentUser?.uid),
          where("projectId", "==", data.id)
        );
        const querySnapshot = await getDocs(q);
        const newData = querySnapshot.docs.map((doc) => {
          return { ...doc.data(), id: doc.id };
        });

        const sortedData = newData.sort((a: any, b: any) => {
          // For date strings in format "YYYY-MM-DD HH:MM:SS" or similar
          // This will sort in descending order (newest first)
          if (!a.date || !b.date) return 0;
          return new Date(b.date).getTime() - new Date(a.date).getTime();
        });

        setReportList(sortedData);
      }
    };

    getReportList();
  }, [data.id]);

  const updateName = async () => {
    if (newName.trim().length === 0) {
      toast.warn(t('common:notifications.nameRequired', "Name cannot be empty"));
      return null;
    }
    
    try {
      const docRef = doc(db, 'projects', data.id);
      
      // Simply update the projectName field
      await updateDoc(docRef, {
        projectName: newName.trim()
      });
      
      toast.success(t('common:notifications.nameUpdated', "Name updated successfully"));
      onStatusChange();
      handleClose();
    } catch (error) {
      console.error('Error updating name: ', error);
      toast.error(t('common:notifications.nameUpdateError', "Error updating name"));
      handleClose();
    }
  };

  return (
    <React.Fragment>
      {data.id && (
        <div className="bg-light activeCard p-2">
          <div className="d-flex justify-content-between align-items-center">
            {/* Display project name exactly as received from backend */}
            <p className="projectName">{data.projectName}</p>
            <Dropdown>
              <Dropdown.Toggle
                className="activeDropdown p-0 mb-2"
                variant="none"
              >
                <Image
                  className="mb-1"
                  src={images.SettingIcon}
                  alt="setting_option"
                />
              </Dropdown.Toggle>

              <Dropdown.Menu>
                <Dropdown.Item
                  onClick={duplicateProject}
                  className="d-flex align-items-center activeDropDownItem"
                >
                  <Image width={20} src={images.DuplicateIcon} />
                  <span className="mx-2">{t('common:actions.duplicate')}</span> 
                </Dropdown.Item>
                <div>
                  <Dropdown.Item
                    onClick={handleShow}
                    className="d-flex align-items-center activeDropDownItem"
                  >
                    <IconWrapper Icon={MdOutlineDriveFileRenameOutline} size={20} color="#6B7783" />
                    <span className="mx-2">{t('common:actions.rename')}</span>
                  </Dropdown.Item>
                  <Dropdown.Item
                    className="d-flex align-items-center activeDropDownItem"
                    onClick={() => {
                      handleClick(data.id);
                    }}
                  >
                    <IconWrapper Icon={FiUpload} size={20} color="#6B7783" />
                    <span className="mx-2">{t('common:actions.changeImage')}</span>
                  </Dropdown.Item>
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                </div>
                <Dropdown.Item
                  onClick={() => setShowArchiveModal(true)}
                  className="d-flex align-items-center activeDropDownItem"
                >
                  <Image width={20} src={images.ArchieveIcon} />
                  <span className="mx-2">{t('activeCards:buttons.archive')}</span>
                </Dropdown.Item>
                <Dropdown.Item
                  onClick={() => setShowDeleteModal(true)}
                  className="d-flex align-items-center activeDropDownItem"
                >
                  <Image width={20} src={images.TrashIcon} />
                  <span className="mx-2">{t('activeCards:buttons.delete')}</span>
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
          <Image
            src={data.projectPhoto}
            style={{ width: "100%", height: "156px", objectFit: "contain" }}
            alt="projectImg"
          />
          <div className="d-block d-md-flex justify-content-between align-items-center mt-2">
            <Dropdown>
              <DropdownToggle variant="none" className="m-0 p-0 border-0">
                <Button className="mt-2 activeCalculated d-flex justify-content-center align-items-center">
                  {reportList.length > 0 ? (
                    <div className="d-flex justify-content-center align-items-center gap-2">
                      <img
                        src={images.Checkedicon}
                        width={20}
                        height={20}
                        style={{ objectFit: "contain" }}
                      />
                      <span> {t('common:results')} </span>
                      <IconWrapper Icon={RxCaretDown}   style={{ marginTop: "1px" }}
                        size={25}
                        color="#000" />
                    </div>
                  ) : (
                    <div className="d-flex justify-content-center align-items-center gap-2">
                      <img
                        src={images.CrossIcon}
                        width={20}
                        height={20}
                        style={{ objectFit: "contain" }}
                      />
                      <span>{t('common:noResults')} </span>
                    </div>
                  )}
                </Button>
              </DropdownToggle>
              {reportList.length > 0 && (
                <DropdownMenu className="reportListDropdownMenu p-3 mt-1">
                  {reportList.map((value) => {
                    return (
                      <DropdownItem
                        onClick={() => {
                          if (value.paymentStatus) {
                            navigate("/paidproject", {
                              state: value.id,
                            });
                          } else {
                            navigate(`/unpaidproject/${value.id}`, {
                              state: { projectId: value.id }, 
                            });
                          }
                        }}
                        key={value.id}
                        className="py-3 px-2 mx-0 mb-3 reportListDropdownList"
                      >
                        <div className="d-flex justify-content-between align-items-center">
                          <p className="m-0 p-0"> Id : {value.id} </p>
                          <p style={{ color: "#0984e3" }} className="m-0 p-0">
                            {" "}
                            {value.paymentStatus ? t('common:paid') : t('common:free')}{" "}
                          </p>
                        </div>
                        <hr style={{ border: "1px solid #000" }} />
                        <p> {t('common:createdAt')} {value.date}</p>
                        <p>
                          {" "}
                          {value.verticalPVArea + value.tiltedPVArea} {t('common:pvAreas')}
                        </p>
                        <p> {value.detectionPoints} {t('common:detectionPoints')}</p>
                      </DropdownItem>
                    );
                  })}
                </DropdownMenu>
              )}
            </Dropdown>
            <Button
              className="mt-2 activeOPenProjects px-2 py-0"
              onClick={() => setShowRelaunchModal(true)}
            >
              {t('activeCards:buttons.view')}
            </Button>
          </div>
        </div>
      )}

      {/* Rename Modal */}
      <Modal show={show} onHide={handleClose} backdrop="static" keyboard={true}>
        <Modal.Header closeButton>
          <Modal.Title>{t('common:actions.rename')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-4">
            <Form.Label style={{ fontSize: "18px" }}>
              {t('common:actions.updateNameHere')}:
            </Form.Label>
            <Form.Control
              type="text"
              value={newName}
              onChange={(e) => {
                setNewName(e.target.value.trimStart());
              }}
            />
            <small className="text-muted mt-2 d-block">
              {t('common:nameLanguageNote', 'The name will be updated in the database as entered here.')}
            </small>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            {t('common:actions.close')}
          </Button>
          <Button variant="primary" onClick={updateName}>{t('common:actions.update')}</Button>
        </Modal.Footer>
      </Modal>

      {/* Archive Confirmation Modal */}
      <Modal show={showArchiveModal} onHide={() => setShowArchiveModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t('activeCards:modals.archive.title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t('activeCards:modals.archive.message')}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowArchiveModal(false)}>
            {t('activeCards:modals.archive.cancel')}
          </Button>
          <Button variant="primary" onClick={moveToArchieve}>
            {t('activeCards:modals.archive.confirm')}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Relaunch Notification Modal */}
      <Modal
        show={showRelaunchModal}
        onHide={() => setShowRelaunchModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>PV-GlareCheck Relaunch</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Please use the new release of the application under{" "}
            <a
              href="https://app2.pv-glarecheck.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              app2.pv-glarecheck.com
            </a>
          </p>
          <p>
            Dear user, PV-GlareCheck had a relaunch, please go here for new
            simulations. Unfortunately, due to extensive changes to the
            database, a new registration is necessary.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            href="https://app2.pv-glarecheck.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            Go to new app
          </Button>
          <Button
            variant="secondary"
            onClick={() => setShowRelaunchModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t('activeCards:modals.delete.title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t('activeCards:modals.delete.message')}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            {t('activeCards:modals.delete.cancel')}
          </Button>
          <Button variant="danger" onClick={moveToTrash}>
            {t('activeCards:modals.delete.confirm')}
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

export default ActiveCard;